import { createBrowserRouter, Navigate } from "react-router-dom"
import { ProtectedRoute } from "@/features/auth/components/ProtectedRoute"
import { DashboardLayout } from "@/app/layout/DashboardLayout"
import { LoginPage } from "@/features/auth/pages/LoginPage"
import { SignupPage } from "@/features/auth/pages/SignupPage"
import { DashboardPage } from "@/features/dashboard/pages/DashboardPage"
import { FarmsPage } from "@/features/farms/pages/FarmsPage"
import { PondsPage } from "@/features/ponds/pages/PondsPage"
import { MorningSummaryPage } from "@/features/morning-summary/MorningSummaryPage"
import { WaterQualityPageNew } from "@/features/water-quality/pages/WaterQualityPageNew"
import { WaterQualityDetailPage } from "@/features/water-quality/pages/WaterQualityDetailPage"
import { CreateWaterQualityPage } from "@/features/water-quality/pages/CreateWaterQualityPage"
import { EditWaterQualityPage } from "@/features/water-quality/pages/EditWaterQualityPage"
import { FishHealthPage } from "@/features/fish-health/FishHealthPage"
import { FishHealthDetailPage } from "@/features/fish-health/FishHealthDetailPage"
import { CreateFishHealthPage } from "@/features/fish-health/pages/CreateFishHealthPage"
import { EditFishHealthPage } from "@/features/fish-health/pages/EditFishHealthPage"
import { MaintenancePage } from "@/features/maintenance/MaintenancePage"
import { MaintenanceDetailPage } from "@/features/maintenance/MaintenanceDetailPage"
import { CreateMaintenancePage } from "@/features/maintenance/pages/CreateMaintenancePage"
import { EditMaintenancePage } from "@/features/maintenance/pages/EditMaintenancePage"
import { FeedPage } from "@/features/feed/FeedPage"
import { FeedDetailPage } from "@/features/feed/FeedDetailPage"
import { CreateFeedRecordPage } from "@/features/feed/pages/CreateFeedRecordPage"
import { EditFeedRecordPage } from "@/features/feed/pages/EditFeedRecordPage"
import { AIInsightsPage, AIAnalysisDetailPage } from "@/features/ai/pages"
import { TeamPage } from "@/features/team/pages/TeamPage"
import { SettingsPage } from "@/features/settings/pages/SettingsPage"
import { LegacyRedirect } from "@/app/components/LegacyRedirect"
import { ErrorBoundary } from "@/shared/components/ErrorBoundary"
import { ROUTES, LEGACY_ROUTES } from "@/shared/lib/constants"

export const router = createBrowserRouter([
  {
    path: ROUTES.LOGIN,
    element: <LoginPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: ROUTES.SIGNUP,
    element: <SignupPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <DashboardLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [
      {
        index: true,
        element: <DashboardPage />,
      },
      // Non-parameterized routes
      {
        path: ROUTES.FARMS,
        element: <FarmsPage />,
      },
      {
        path: ROUTES.TEAM,
        element: <TeamPage />,
      },
      {
        path: ROUTES.SETTINGS,
        element: <SettingsPage />,
      },
      // Farm-scoped routes
      {
        path: "farms/:farmId",
        children: [
          // Farm detail shows Recent Overview
          {
            index: true,
            element: <MorningSummaryPage />,
          },
          // Ponds list and detail
          {
            path: "ponds",
            children: [
              {
                index: true,
                element: <PondsPage />,
              },
              {
                path: ":pondId",
                children: [
                  {
                    index: true,
                    element: <PondsPage />, // Same component, modal shown based on pondId
                  },
                  {
                    path: "water-quality/:testId",
                    element: <WaterQualityDetailPage />,
                  },
                ],
              },
            ],
          },
          // Farm-level features with pond filtering via query params
          {
            path: "water-quality",
            children: [
              {
                index: true,
                element: <WaterQualityPageNew />,
              },
              {
                path: "trends",
                element: <WaterQualityPageNew />,
              },
              {
                path: "new",
                element: <CreateWaterQualityPage />,
              },
              {
                path: ":testId/edit",
                element: <EditWaterQualityPage />,
              },
            ],
          },
          {
            path: "fish-health",
            children: [
              {
                index: true,
                element: <FishHealthPage />,
              },
              {
                path: "trends",
                element: <FishHealthPage />,
              },
              {
                path: "new",
                element: <CreateFishHealthPage />,
              },
              {
                path: ":checkId",
                element: <FishHealthDetailPage />,
              },
              {
                path: ":checkId/edit",
                element: <EditFishHealthPage />,
              },
            ],
          },
          {
            path: "maintenance",
            children: [
              {
                index: true,
                element: <MaintenancePage />,
              },
              {
                path: "trends",
                element: <MaintenancePage />,
              },
              {
                path: "alerts",
                element: <MaintenancePage />,
              },
              {
                path: "new",
                element: <CreateMaintenancePage />,
              },
              {
                path: ":checkId",
                element: <MaintenanceDetailPage />,
              },
              {
                path: ":checkId/edit",
                element: <EditMaintenancePage />,
              },
            ],
          },
          {
            path: "feed",
            children: [
              {
                index: true,
                element: <FeedPage />,
              },
              {
                path: "trends",
                element: <FeedPage />,
              },
              {
                path: "alerts",
                element: <FeedPage />,
              },
              {
                path: "new",
                element: <CreateFeedRecordPage />,
              },
              {
                path: ":recordId",
                element: <FeedDetailPage />,
              },
              {
                path: ":recordId/edit",
                element: <EditFeedRecordPage />,
              },
            ],
          },
          // AI Insights
          {
            path: "ai",
            children: [
              {
                index: true,
                element: <Navigate to="root-cause" replace />,
              },
              {
                path: "root-cause",
                element: <AIInsightsPage />,
              },
              {
                path: "diagnosis",
                element: <AIInsightsPage />,
              },
              {
                path: "history",
                element: <AIInsightsPage />,
              },
              {
                path: ":analysisId",
                element: <AIAnalysisDetailPage />,
              },
            ],
          },
        ],
      },
      // Legacy route redirects
      {
        path: LEGACY_ROUTES.PONDS,
        element: <LegacyRedirect to="ponds" />,
      },
      {
        path: LEGACY_ROUTES.WATER_QUALITY,
        element: <LegacyRedirect to="water-quality" />,
      },
      {
        path: LEGACY_ROUTES.FISH_HEALTH,
        element: <LegacyRedirect to="fish-health" />,
      },
      {
        path: LEGACY_ROUTES.MAINTENANCE,
        element: <LegacyRedirect to="maintenance" />,
      },
      {
        path: LEGACY_ROUTES.FEED,
        element: <LegacyRedirect to="feed" />,
      },
      {
        path: LEGACY_ROUTES.AI,
        element: <LegacyRedirect to="ai" />,
      },
    ],
  },
  {
    path: "*",
    element: <Navigate to={ROUTES.HOME} replace />,
  },
])
